import { Link } from "react-router-dom";
import {
  PendingDecision,
  PolicyDocument,
  PolicyList,
  PolicySection,
  PolicyText,
} from "@/components/PolicyDocument";
import { AMAZON_ASSOCIATES_DISCLOSURE } from "@/lib/affiliate-disclosure";

/**
 * The terms of service.
 *
 * Two things here are compliance surfaces rather than prose, and both are
 * pinned by tests:
 *
 *   1. `AMAZON_ASSOCIATES_DISCLOSURE` — the sentence the Associates Operating
 *      Agreement §5 requires verbatim. It is unconditional here on purpose: it
 *      describes the *relationship*, which exists for the entity, whereas the
 *      paragraph on the results page describes *those links*, which a
 *      deployment without a tag does not earn on. See docs/policy-revisions.md.
 *   2. The double-disclosure in "How we are paid" — CLAUDE.md's rule is that
 *      the commission and the fee must be stated in the same place. Splitting
 *      them across two sections would technically disclose both and would
 *      still be the quiet double-dip the rule exists to prevent.
 *
 * The "Prices and availability" section is deliberately unfinished: see #166.
 * Do not complete it in either direction without that decision.
 */
export function TermsPage() {
  return (
    <PolicyDocument title="Terms of service" reviewed="2026-08-16">
      <PolicyText>
        These terms cover using Pickwell. They describe the product as it works today, and where a
        decision has not been made they say so rather than guessing.
      </PolicyText>

      <PolicySection id="what-pickwell-is" heading="What Pickwell is">
        <PolicyText>
          Pickwell is an advisor. We do not sell, stock, ship, service or warrant anything. Every
          product you see is sold by Amazon or by a seller on Amazon, under their terms, their
          pricing and their returns policy. Our part is working out which few options are worth your
          attention, and telling you why the rest are not.
        </PolicyText>
      </PolicySection>

      <PolicySection id="how-we-are-paid" heading="How we are paid — both halves, in one place">
        <p className="rounded-lg border p-4 text-base font-medium">
          {AMAZON_ASSOCIATES_DISCLOSURE}
        </p>
        <PolicyText>
          In plain terms: Pickwell takes part in the Amazon Associates Program. The product links we
          show carry a tracking tag, and if you buy after following one, Amazon pays us a commission
          — at no extra cost to you.
        </PolicyText>
        <PolicyText>
          Pickwell also quotes a fee for each search. Today every quoted fee is waived: no payment
          processor is connected to the product and nothing has ever been charged. The intention is
          to charge, and when we do we will still be earning the Amazon commission as well. Both, at
          once. We would rather you read that here than work it out later.
        </PolicyText>
      </PolicySection>

      <PolicySection id="recommendations" heading="What we promise about recommendations">
        <PolicyList>
          <li>
            <strong>Ranking is not for sale.</strong> No seller, brand or vendor can pay to appear
            in a shortlist, to rank higher in one, or to avoid being set aside.
          </li>
          <li>
            <strong>The commission does not enter the decision.</strong> Requirements and
            listing-integrity signals decide what survives; the tracking tag is attached afterwards,
            to whatever did.
          </li>
          <li>
            <strong>We will give you the unwelcome answer.</strong> That none of these are worth
            buying, or that the cheap one is fine, are answers Pickwell is built to be able to give.
            A store's own assistant cannot.
          </li>
          <li>
            <strong>We do not give timing advice.</strong> Nothing in Pickwell is designed to tell
            you when to buy.
          </li>
          <li>
            <strong>Signals are described, not alleged.</strong> Where something in a listing's
            history looks unusual, we tell you what was observed and over what period, and leave the
            conclusion to you. We do not accuse anyone of anything.
          </li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="prices" heading="Prices and availability">
        <PendingDecision id="price-display">
          How Pickwell presents Amazon price and availability information is under review: the
          Associates Program Policies place conditions on where such data may come from, and the
          review is tracked as issue #166. This section is intentionally blank until that is
          settled, and it describes current behaviour in neither direction.
        </PendingDecision>
        <PolicyText>
          Independent of that review, one thing holds either way: what a product costs and whether
          it is in stock are Amazon's to state, and they change. Amazon's own product page at the
          moment of purchase is the only authoritative source for both, and it is what is shown
          there that you will pay.
        </PolicyText>
      </PolicySection>

      <PolicySection id="no-warranty" heading="No warranty on what you buy">
        <PolicyText>
          A Pickwell recommendation is an opinion formed from third-party data. We do not warrant
          that a recommended product is fit for your purpose, safe for a particular use, in stock,
          authentic, durable or the best one available. The data we work from can be incomplete or
          out of date, and a listing can change after we have read it.
        </PolicyText>
        <PolicyText>
          You make the buying decision, and the seller and the manufacturer stand behind the
          product, not us. Where the stakes are physical — anything a child uses unattended,
          anything electrical, anything load-bearing — treat a shortlist as a starting point and
          read the seller's and manufacturer's own documentation before you rely on it.
        </PolicyText>
        <PolicyText>
          To the fullest extent the law allows, Pickwell is provided “as is”, without warranties of
          any kind.
        </PolicyText>
        <PendingDecision id="liability">
          The limit of our liability, and the law and forum that would decide a dispute about it,
          are part of the same unsettled entity question this page records under “Changes and
          contact”. This page states no cap it has not been authorised to state.
        </PendingDecision>
      </PolicySection>

      <PolicySection id="your-account" heading="Your account, and fair use">
        <PolicyList>
          <li>
            One account per person. Keep your sign-in to yourself, and tell us if you think someone
            else has it.
          </li>
          <li>
            Every search spends metered product data from a budget shared by everyone using
            Pickwell. We may rate-limit or suspend an account that consumes it abusively, including
            automated querying.
          </li>
          <li>
            Do not scrape Pickwell, resell its output, or use it to reconstruct the underlying
            vendor data — our own agreements with data suppliers do not permit us to pass that on.
          </li>
          <li>We may change, suspend or withdraw the service, or any part of it.</li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="signed-out" heading="If you are not signed in">
        <PolicyText>
          Searching requires an account today, because a search spends shared metered data. You can
          read this page, the privacy policy and the home page without one. The parts of these terms
          about accounts apply once you have an account; the rest apply to reading the site. If
          Pickwell later allows searching without an account, this is the section that changes.
        </PolicyText>
      </PolicySection>

      <PolicySection id="your-data" heading="Your data">
        <PolicyText>
          The{" "}
          <Link to="/privacy" className="underline underline-offset-4 hover:text-foreground">
            privacy policy
          </Link>{" "}
          is the full account. In short: we do not store the questions we ask you or the answers you
          give; we do store your words — verbatim in two specific cases, and shortened into the
          category label of every search — and several of those copies carry no account link and are
          kept indefinitely; and the text you send is retained by the AI gateway our infrastructure
          provider runs, separately from our own database.
        </PolicyText>
      </PolicySection>

      <PolicySection id="changes" heading="Changes and contact">
        <PolicyText>
          When how we are paid or what we promise changes, this page changes in the same release.
        </PolicyText>
        <PendingDecision id="entity">
          Pickwell is a 2amlogic property. The legal entity behind it, its registered address, the
          law governing these terms and the forum for any dispute are not settled, so this page does
          not name them.
        </PendingDecision>
        <PendingDecision id="contact">
          There is no contact address to publish yet. Where to send a question about these terms is
          part of the same unsettled decision as the deletion-request address in the privacy policy.
        </PendingDecision>
      </PolicySection>
    </PolicyDocument>
  );
}
